import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { GoogleMap } from '@react-google-maps/api';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext.jsx';
import { useTranslation } from '../../context/TranslationContext.jsx';
import './MapComponent.css';

const parseLocalYmdStartMs = (dateStr) => {
  if (!dateStr || typeof dateStr !== 'string') return null;
  // Treat YYYY-MM-DD as local date to avoid UTC parsing edge cases.
  const d = new Date(`${dateStr}T00:00:00`);
  const ms = d.getTime();
  return Number.isNaN(ms) ? null : ms;
};

const isPastLocalYmd = (dateStr, todayStartMs) => {
  const ms = parseLocalYmdStartMs(dateStr);
  return ms != null && ms < todayStartMs;
};

const MapComponent = ({ events, filteredEvents }) => {
  const center = { lat: 39.9334, lng: 32.8597 };
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const { translationEnabled, tEvent, tStatic } = useTranslation();
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const viewEventText = tStatic('Etkinliği Görüntüle');
  const eventFallbackText = tStatic('Etkinlik');

  const mapId = useMemo(() => {
    const fromEnv = isDarkMode
      ? process.env.REACT_APP_GOOGLE_MAP_ID_DARK
      : process.env.REACT_APP_GOOGLE_MAP_ID_LIGHT;

    return fromEnv || process.env.REACT_APP_GOOGLE_MAP_ID || '6100ebed9733d3ab';
  }, [isDarkMode]);

  const todayStartMs = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, []);

  const markersToDisplay = useMemo(() => {
    const shouldUseFiltered = Array.isArray(filteredEvents);
    const source = shouldUseFiltered ? filteredEvents : events;
    const list = Array.isArray(source) ? source : [];

    // Do not remove past events from the site, only from the map.
    return list
      .filter((event) => event && event.status !== 'pending')
      .filter((event) => !isPastLocalYmd(event.date, todayStartMs))
      .filter((event) => event?.coordinates?.lat && event?.coordinates?.lng);
  }, [events, filteredEvents, todayStartMs]);

  const createMarkerContent = useCallback((event) => {
    const container = document.createElement('div');
    container.className = 'event-marker';
    container.setAttribute('role', 'button');
    container.setAttribute('tabindex', '0');
    container.setAttribute('aria-label', `${viewEventText}: ${event?.name ?? eventFallbackText}`);

    const pin = document.createElement('div');
    pin.className = 'event-marker-pin';

    const label = document.createElement('div');
    label.className = 'event-marker-label';
    const rawName = event?.name ?? '';
    label.textContent = rawName;

    if (translationEnabled && rawName) {
      const out = tEvent(rawName);
      if (out && typeof out.then === 'function') {
        out.then((translated) => {
          if (typeof translated === 'string' && translated) label.textContent = translated;
        }).catch(() => {
          // ignore
        });
      } else if (typeof out === 'string' && out) {
        label.textContent = out;
      }
    }

    container.appendChild(pin);
    container.appendChild(label);

    // Keyboard support (Enter/Space).
    container.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        navigate(`/etkinlik/${event.id}`);
      }
    });

    // Clicking the content should navigate even if the gmp-click event doesn't fire.
    container.addEventListener('click', () => navigate(`/etkinlik/${event.id}`));

    return container;
  }, [eventFallbackText, navigate, tEvent, translationEnabled, viewEventText]);

  const renderMarkers = useCallback(
    (map) => {
      if (!map || !window.google) return;

      // Clear existing markers
      markersRef.current.forEach((marker) => {
        if (marker) marker.setMap(null);
      });
      markersRef.current = [];

      markersToDisplay.forEach((event) => {
        try {
          const marker = new window.google.maps.marker.AdvancedMarkerElement({
            position: {
              lat: event.coordinates.lat,
              lng: event.coordinates.lng,
            },
            map,
            title: event?.name ?? eventFallbackText,
            content: createMarkerContent(event),
          });

          // Prefer the official advanced marker click event if supported.
          if (typeof marker.addListener === 'function') {
            marker.addListener('gmp-click', () => navigate(`/etkinlik/${event.id}`));
          }

          markersRef.current.push(marker);
        } catch (error) {
          console.error('Error creating marker:', error);
        }
      });
    },
    [createMarkerContent, eventFallbackText, markersToDisplay, navigate]
  );

  const applyThemedMapOptions = useCallback(
    (map) => {
      if (!map || !window.google) return;

      try {
        const ColorScheme = window.google?.maps?.ColorScheme;
        map.setOptions({
          mapId,
          ...(ColorScheme ? { colorScheme: isDarkMode ? ColorScheme.DARK : ColorScheme.LIGHT } : {}),
        });
      } catch (error) {
        // Non-fatal: Map will just use default styling.
        console.error('Error applying themed map options:', error);
      }
    },
    [isDarkMode, mapId]
  );

  useEffect(() => {
    return () => {
      if (markersRef.current.length > 0) {
        markersRef.current.forEach(marker => {
          if (marker) marker.setMap(null);
        });
        markersRef.current = [];
      }
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (mapRef.current) renderMarkers(mapRef.current);
  }, [renderMarkers]);

  useEffect(() => {
    if (mapRef.current) applyThemedMapOptions(mapRef.current);
  }, [applyThemedMapOptions]);

  return (
    <div className="map-container">
      <GoogleMap
        onLoad={(map) => {
          mapRef.current = map;
          applyThemedMapOptions(map);
          renderMarkers(map);
        }}
        mapContainerClassName="map"
        zoom={4}
        center={center}
        options={{
          mapId,
          clickableIcons: false,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: true,
          gestureHandling: 'cooperative',
          backgroundColor: isDarkMode ? '#0b1220' : '#f7f9fc',
        }}
      />
    </div>
  );
};

export default MapComponent;
